import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const PLANS = ['starter', 'club', 'pro'];

export default function SuperAdmin() {
  const [apiKey, setApiKey] = useState('');
  const [authenticated, setAuthenticated] = useState(false);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [newTenant, setNewTenant] = useState({ name: '', subdomain: '', contact_email: '', plan: 'starter' });
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');
  const [createSuccess, setCreateSuccess] = useState('');
  const [newCredentials, setNewCredentials] = useState(null);
  const [copiedSnippet, setCopiedSnippet] = useState(false);

  const getTraefikSnippet = (subdomain) => `http:
  routers:
    courtcraft-${subdomain}:
      rule: "Host(\`${subdomain}.courtcraft.tmk-it.de\`)"
      entryPoints:
        - "websecure"
      tls:
        certResolver: "letsencrypt"
      service: "courtcraft"`;

  const copySnippet = (subdomain) => {
    navigator.clipboard.writeText(getTraefikSnippet(subdomain));
    setCopiedSnippet(true);
    setTimeout(() => setCopiedSnippet(false), 2000);
  };

  const fetchTenants = useCallback(async (key) => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get('/admin/tenants', { headers: { 'X-Api-Key': key } });
      setTenants(res.data);
      setAuthenticated(true);
    } catch (err) {
      if (err.response?.status === 403) {
        setError('Ungültiger API-Key');
        setAuthenticated(false);
      } else {
        setError('Fehler beim Laden der Tenants');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (apiKey) fetchTenants(apiKey);
  }, []);

  const handleLogin = (e) => {
    e.preventDefault();
    fetchTenants(apiKey);
  };

  const apiHeaders = { 'X-Api-Key': apiKey };

  const handleToggleActive = async (tenant) => {
    try {
      await axios.put(`/admin/tenants/${tenant.id}?is_active=${!tenant.is_active}`, null, { headers: apiHeaders });
      fetchTenants(apiKey);
    } catch {
      setError('Fehler beim Aktualisieren');
    }
  };

  const handleChangePlan = async (tenant, plan) => {
    try {
      await axios.put(`/admin/tenants/${tenant.id}?plan=${plan}`, null, { headers: apiHeaders });
      fetchTenants(apiKey);
    } catch {
      setError('Fehler beim Ändern des Plans');
    }
  };

  const handleMigrate = async (tenant) => {
    try {
      const res = await axios.post(`/admin/tenants/${tenant.id}/migrate`, null, { headers: apiHeaders });
      const applied = res.data.applied;
      alert(applied.length > 0
        ? `✓ ${applied.length} Migration(en) angewendet: ${applied.join(', ')}`
        : '✓ Keine ausstehenden Migrations');
    } catch {
      setError('Fehler beim Ausführen der Migrations');
    }
  };

  const handleMigrateAll = async () => {
    try {
      const res = await axios.post('/admin/migrate-all', null, { headers: apiHeaders });
      const results = res.data.results;
      const summary = Object.entries(results)
        .map(([schema, ran]) => `${schema}: ${ran.length > 0 ? ran.join(', ') : 'aktuell'}`)
        .join('\n');
      alert(`Migration abgeschlossen:\n${summary}`);
    } catch {
      setError('Fehler beim Migrieren aller Tenants');
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    setCreateError('');
    setCreateSuccess('');
    try {
      const params = new URLSearchParams({
        name: newTenant.name,
        subdomain: newTenant.subdomain,
        contact_email: newTenant.contact_email,
        plan: newTenant.plan,
      });
      const res = await axios.post(`/admin/tenants?${params}`, null, { headers: apiHeaders });
      setCreateSuccess(`✓ ${res.data.message}`);
      setNewCredentials({
        subdomain: res.data.subdomain,
        username: res.data.admin_username,
        password: res.data.admin_password,
      });
      setNewTenant({ name: '', subdomain: '', contact_email: '', plan: 'starter' });
      fetchTenants(apiKey);
    } catch (err) {
      setCreateError(err.response?.data?.detail || 'Fehler beim Anlegen');
    } finally {
      setCreating(false);
    }
  };

  if (!authenticated) {
    return (
      <div className="max-w-sm mx-auto p-4 mt-20">
        <div className="bg-white dark:bg-slate-800 rounded-xl p-8 shadow-md">
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-1">Superadmin</h1>
          <p className="text-slate-500 dark:text-slate-400 text-sm mb-6">Tenant-Verwaltung · CourtCraft</p>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block font-semibold text-sm text-slate-900 dark:text-white mb-1">API-Key</label>
              <input
                type="password"
                placeholder="API-Key eingeben"
                value={apiKey}
                onChange={e => setApiKey(e.target.value)}
                className="w-full px-4 py-2.5 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-base focus:outline-none focus:border-gold transition-colors"
                autoFocus
              />
            </div>
            {error && (
              <div className="px-4 py-3 rounded-lg text-sm bg-red-500/10 border border-red-500 text-red-500">{error}</div>
            )}
            <button
              type="submit"
              disabled={loading}
              className="w-full btn btn-primary"
            >
              {loading ? 'Wird geprüft…' : 'Anmelden'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-4">
      {/* Header */}
      <div className="bg-white dark:bg-slate-800 rounded-xl p-6 shadow-md mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Tenant-Verwaltung</h1>
            <p className="text-slate-500 dark:text-slate-400 text-sm mt-1">Superadmin · CourtCraft</p>
          </div>
          <div className="flex gap-2">
            <button onClick={handleMigrateAll} className="btn btn-secondary text-sm">
              Alle migrieren
            </button>
            <button
              onClick={() => { setAuthenticated(false); sessionStorage.removeItem('superadmin_key'); }}
              className="btn btn-secondary text-sm"
            >
              Abmelden
            </button>
          </div>
        </div>
      </div>

      {/* Neuen Tenant anlegen */}
      <div className="bg-white dark:bg-slate-800 rounded-xl p-6 shadow-md mb-6">
        <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-4 pb-2 border-b-2 border-slate-200 dark:border-slate-700">
          Neuen Club anlegen
        </h2>
        <form onSubmit={handleCreate} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="p-4 bg-slate-50 dark:bg-slate-700/50 border border-slate-200 dark:border-slate-600 rounded-lg">
            <label className="block font-semibold text-sm text-slate-900 dark:text-white mb-1">Club-Name</label>
            <input
              type="text"
              placeholder="z.B. X3 Padel Hamburg"
              value={newTenant.name}
              onChange={e => setNewTenant(p => ({ ...p, name: e.target.value }))}
              required
              className="w-full px-4 py-2.5 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-base focus:outline-none focus:border-gold transition-colors"
            />
          </div>
          <div className="p-4 bg-slate-50 dark:bg-slate-700/50 border border-slate-200 dark:border-slate-600 rounded-lg">
            <label className="block font-semibold text-sm text-slate-900 dark:text-white mb-1">Subdomain</label>
            <input
              type="text"
              placeholder="z.B. x3hamburg"
              value={newTenant.subdomain}
              onChange={e => setNewTenant(p => ({ ...p, subdomain: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') }))}
              required
              className="w-full px-4 py-2.5 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-base focus:outline-none focus:border-gold transition-colors"
            />
            {newTenant.subdomain && (
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{newTenant.subdomain}.courtcraft.app</p>
            )}
          </div>
          <div className="p-4 bg-slate-50 dark:bg-slate-700/50 border border-slate-200 dark:border-slate-600 rounded-lg">
            <label className="block font-semibold text-sm text-slate-900 dark:text-white mb-1">Kontakt-E-Mail</label>
            <input
              type="email"
              placeholder="kontakt@club.de"
              value={newTenant.contact_email}
              onChange={e => setNewTenant(p => ({ ...p, contact_email: e.target.value }))}
              className="w-full px-4 py-2.5 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-base focus:outline-none focus:border-gold transition-colors"
            />
          </div>
          <div className="p-4 bg-slate-50 dark:bg-slate-700/50 border border-slate-200 dark:border-slate-600 rounded-lg">
            <label className="block font-semibold text-sm text-slate-900 dark:text-white mb-1">Plan</label>
            <select
              value={newTenant.plan}
              onChange={e => setNewTenant(p => ({ ...p, plan: e.target.value }))}
              className="w-full px-4 py-2.5 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-base focus:outline-none focus:border-gold transition-colors"
            >
              {PLANS.map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
            </select>
          </div>
          <div className="sm:col-span-2 flex items-center gap-4 flex-wrap">
            <button type="submit" disabled={creating} className="btn btn-primary">
              {creating ? 'Wird angelegt…' : 'Club anlegen'}
            </button>
            {createError && (
              <span className="text-red-500 text-sm">{createError}</span>
            )}
            {createSuccess && (
              <span className="text-green-500 text-sm">{createSuccess}</span>
            )}
          </div>
        </form>

        {newCredentials && (
          <div className="mt-4 space-y-3">
            {/* Login-Daten */}
            <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-300 dark:border-green-700 rounded-lg">
              <p className="font-semibold text-green-800 dark:text-green-300 mb-2">✓ Club angelegt — Login-Daten notieren!</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm">
                <div>
                  <span className="text-slate-500 dark:text-slate-400">URL</span>
                  <p className="font-mono text-slate-900 dark:text-white">{newCredentials.subdomain}.courtcraft.tmk-it.de</p>
                </div>
                <div>
                  <span className="text-slate-500 dark:text-slate-400">Benutzername</span>
                  <p className="font-mono text-slate-900 dark:text-white">{newCredentials.username}</p>
                </div>
                <div>
                  <span className="text-slate-500 dark:text-slate-400">Passwort</span>
                  <p className="font-mono text-slate-900 dark:text-white">{newCredentials.password}</p>
                </div>
              </div>
            </div>

            {/* Traefik-Config */}
            <div className="p-4 bg-slate-50 dark:bg-slate-700/50 border border-slate-200 dark:border-slate-600 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <p className="font-semibold text-slate-900 dark:text-white text-sm">Traefik-Config — in neue Datei <span className="font-mono text-yellow-600 dark:text-yellow-400">courtcraft-{newCredentials.subdomain}.yml</span> einfügen</p>
                <button
                  onClick={() => copySnippet(newCredentials.subdomain)}
                  className="text-xs px-3 py-1 rounded-lg bg-slate-200 dark:bg-slate-600 hover:bg-slate-300 dark:hover:bg-slate-500 text-slate-700 dark:text-slate-200 transition-colors"
                >
                  {copiedSnippet ? '✓ Kopiert!' : 'Kopieren'}
                </button>
              </div>
              <pre className="text-xs font-mono text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-800 p-3 rounded-lg overflow-x-auto border border-slate-200 dark:border-slate-600">
                {getTraefikSnippet(newCredentials.subdomain)}
              </pre>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">
                Danach DNS: <span className="font-mono">{newCredentials.subdomain}.courtcraft.tmk-it.de</span> → Traefik-Server CNAME eintragen
              </p>
            </div>

            <button
              onClick={() => { setNewCredentials(null); setCreateSuccess(''); }}
              className="text-xs text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
            >
              Schließen
            </button>
          </div>
        )}
      </div>

      {/* Tenant-Liste */}
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-md overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700">
          <h2 className="text-xl font-semibold text-slate-900 dark:text-white">
            Alle Clubs <span className="text-slate-400 font-normal text-base">({tenants.length})</span>
          </h2>
          <button onClick={() => fetchTenants(apiKey)} className="btn btn-secondary text-sm">
            Aktualisieren
          </button>
        </div>

        {error && (
          <div className="px-6 py-3 text-sm bg-red-500/10 border-b border-red-500/20 text-red-500">{error}</div>
        )}

        {tenants.length === 0 ? (
          <p className="text-slate-500 dark:text-slate-400 px-6 py-10 text-center">Noch keine Clubs angelegt.</p>
        ) : (
          <div className="divide-y divide-slate-200 dark:divide-slate-700">
            {tenants.map(tenant => (
              <div key={tenant.id} className="px-6 py-4 flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-slate-900 dark:text-white">{tenant.name}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                      tenant.is_active
                        ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300'
                        : 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300'
                    }`}>
                      {tenant.is_active ? 'Aktiv' : 'Inaktiv'}
                    </span>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300">
                      {tenant.plan}
                    </span>
                  </div>
                  <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
                    {tenant.subdomain}.courtcraft.app
                    {tenant.contact_email && <span className="ml-3">{tenant.contact_email}</span>}
                  </p>
                  <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">Schema: {tenant.schema_name}</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <select
                    value={tenant.plan}
                    onChange={e => handleChangePlan(tenant, e.target.value)}
                    className="px-3 py-1.5 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-sm focus:outline-none focus:border-gold transition-colors"
                  >
                    {PLANS.map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
                  </select>
                  <button
                    onClick={() => handleMigrate(tenant)}
                    className="text-sm rounded-lg px-3 py-1.5 font-medium transition-colors border bg-blue-50 dark:bg-blue-900/20 border-blue-300 dark:border-blue-700 text-blue-600 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/40"
                    title="Ausstehende Migrations ausführen"
                  >
                    Migrieren
                  </button>
                  <button
                    onClick={() => handleToggleActive(tenant)}
                    className={`text-sm rounded-lg px-4 py-1.5 font-medium transition-colors border ${
                      tenant.is_active
                        ? 'bg-red-50 dark:bg-red-900/20 border-red-300 dark:border-red-700 text-red-600 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-900/40'
                        : 'bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-700 text-green-600 dark:text-green-300 hover:bg-green-100 dark:hover:bg-green-900/40'
                    }`}
                  >
                    {tenant.is_active ? 'Deaktivieren' : 'Aktivieren'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
